/* eslint max-lines: off */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useAsync } from 'react-use';
import { useHistory } from 'react-router';
import { Channel } from 'utils/enums';

import { OpenConvalidaResponse, Group, Task, Definition, GetWfHistoryResponse } from 'models/response';

import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import actions from 'state/actions';

import TextArea from 'components/Input/Textarea';
import Table from 'components/Table';
import Modal from 'components/Modal';
import styled from 'styled-components/macro';

import Groups from './groups';
import NextTask from './nextTask';
import Close from './close';
import Reference from './Reference/index';
import { CloseConvalidateDocumentBody } from 'models/request';
import { useIntl } from 'react-intl';
import { fontSizePalette, fontWeightPalette } from 'utils/styleConstants';

const Section = styled.div`
  margin: 30px 0;
`;

export const Title = styled.h4`
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${fontSizePalette.medium};
  font-weight: ${fontWeightPalette.medium};
  letter-spacing: -0.5px;
  line-height: 19px;
  margin-right: 8px;
  margin-bottom: 10px;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

export const Left = styled.div`
  width: 220px;
`;

export const Right = styled.div`
  width: 100%;
  max-height: 350px;
  overflow: auto;
  margin-left: 10px;
  padding-right: 10px;
`;

export const CardContainer = styled.div`
  max-height: 350px;
  overflow: auto;
  padding-right: 15px;
`;

type Mode = 'groups' | 'nextTask' | 'reference' | 'close';

const Convalida = () => {
  const t = utils.intl.useTranslator();
  const useintl = useIntl();
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const [openConvalidateResponse, setOpenConvalidateResponse] = useState<OpenConvalidaResponse | null>(null);
  const [rowsHistory, setRowsHistory] = useState<GetWfHistoryResponse[]>([]);
  const [note, setNote] = useState<string>('');
  const history = useHistory();

  const [mode, setMode] = useState<Mode | null>(null);
  const [selectedDefinition, setSelectedDefinition] = useState<Definition | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [noteLog, setNoteLog] = useState<string>('');
  const userPreference = useSelector(selectors.user.getUserPreference);
  const activeDocument = useSelector(selectors.documents.selectActiveDocument);

  useEffect(() => {
    setSelectedDefinition(null);
    setSelectedTask(null);
    setSelectedGroups([]);
  }, [selectedTab]);

  const updateSelectedTask = (isActive?: boolean, task?: Task) => {
    if (isActive && task) {
      setSelectedTask(task);
      setSelectedGroups([]);
    } else {
      setSelectedTask(null);
      setSelectedGroups([]);
    }
  };

  useAsync(async () => {
    try {
      if (protocol && activeDocument?.protocolIn) {
        const { data } = await service.openConvalidate(protocol);
        const historyResponse = await service.getWfHistory(protocol);
        setRowsHistory(
          historyResponse.data.map((e) => {
            return {
              ...e,
              barcode: activeDocument?.protocolIn,
              channel: Channel[activeDocument?.incChannel as number],
            };
          }),
        );
        setOpenConvalidateResponse(data);

        const stringakey = data?.defaultNoteLog;
        const accessToKey = useintl.messages[`${stringakey}.localKeyValue`];

        if (accessToKey) {
          setNoteLog(accessToKey as string);
        } else {
          setNoteLog(stringakey);
        }

        const getMode = () => {
          if (data.definitionClosable === true && data.reference === null) return 'close';
          if (data.definitionClosable === false && data.escalationGroups !== null) return 'groups';
          if (data.definitionClosable === false && data.referenceNextTask !== null && data.reference === null) {
            return 'nextTask';
          }
          if (data.referenceNextTask !== null && data.reference !== null) return 'reference';
          return null;
        };
        setMode(getMode());
        if (
          getMode() === 'nextTask' &&
          data.referenceNextTask?.referenceNextTask.length === 1 &&
          data.referenceNextTask?.referenceNextTask[0].taskAction?.idAction === 2
        ) {
          setSelectedTask(data.referenceNextTask?.referenceNextTask[0]);
        }
      }
    } catch (err) {
      dispatch(actions.modal.closeModal());
      console.error(err);
    }
  }, []);

  const storicoColumns = [
    {
      accessor: 'causalCode',
      Header: t('causalCode'),
    },
    { accessor: 'note', Header: t('note') },
    { accessor: 'barcode', Header: t('barcode') },
    { accessor: 'channel', Header: t('channel') },
    {
      accessor: 'rejectionReason',
      Header: t('rejectionReason'),
      Cell: ({ value }: { value: null | string }) => value ?? '',
    },
    {
      accessor: 'sender',
      Header: t('sender'),
      Cell: ({ value }: { value: { name: string } }) => value.name ?? '',
    },
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, userPreference.timeZone ?? 'UTC'),
    },
  ];

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const handleConfirm = async () => {
    try {
      if (protocol && openConvalidateResponse) {
        const body = (): CloseConvalidateDocumentBody | null => {
          switch (mode) {
            case 'nextTask':
              return {
                protocol,
                note,
                referenceNextTaskId: {
                  isLastTask: openConvalidateResponse.referenceNextTask?.lastTask || false,
                  wfTaskGroups: {
                    idGroup: selectedGroups.map((e) => e.idGroup),
                    idTaskDefinition: selectedTask?.idTaskDefinition || null,
                  },
                },
                escalationId: null,
                referenceId: null,
                definitionClosable: openConvalidateResponse.definitionClosable,
                finalDestinationUser: null,
              };
            case 'groups':
              return {
                protocol,
                note,
                referenceNextTaskId: null,
                escalationId: {
                  wfTaskGroups: {
                    idGroup: selectedGroups.map((e) => e.idGroup),
                    idTaskDefinition: selectedGroups.length ? selectedGroups[0].groupTasks?.idTaskDefinition : null,
                  },
                },
                referenceId: null,
                definitionClosable: openConvalidateResponse.definitionClosable,
                finalDestinationUser: null,
              };
            case 'close':
              return {
                protocol,
                note,
                referenceNextTaskId: null,
                escalationId: null,
                referenceId: null,
                definitionClosable: openConvalidateResponse.definitionClosable,
                finalDestinationUser: openConvalidateResponse.finalDestinationUser?.idUser || 0,
              };
            case 'reference':
              if (selectedTab === 0) {
                return {
                  protocol,
                  note,
                  referenceNextTaskId: {
                    isLastTask: openConvalidateResponse.referenceNextTask?.lastTask || false,
                    wfTaskGroups: {
                      idGroup: selectedGroups.map((e) => e.idGroup),
                      idTaskDefinition: selectedTask?.idTaskDefinition || null,
                    },
                  },
                  escalationId: null,
                  referenceId: null,
                  definitionClosable: openConvalidateResponse.definitionClosable,
                  finalDestinationUser: null,
                };
              } else {
                return {
                  protocol,
                  note,
                  referenceNextTaskId: null,
                  escalationId: null,
                  referenceId: {
                    isLastTask: false,
                    wfTaskGroups: {
                      idGroup: selectedGroups.map((e) => e.idGroup),
                      idTaskDefinition: selectedTask?.idTaskDefinition || null,
                    },
                    idCompany: selectedDefinition?.idCompany,
                    idDefinition: selectedDefinition?.idDefinition || null,
                  },
                  definitionClosable: false,
                  finalDestinationUser: null,
                };
              }
            default:
              return null;
          }
        };

        const bodyApi = body();

        if (bodyApi) {
          await service.closeConvalidateDocument(bodyApi);
          dispatch(actions.modal.closeModal());
          dispatch(actions.documents.removeRows([protocol]));
          utils.app.notify('success', t('success_convalidate'));
          if (utils.documents.isOpenDocumentView()) dispatch(actions.documents.openNextDocument(history));
        } else {
          utils.app.notify('fail', t('api_param_missing'));
          throw new Error('api_param_missing');
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const renderContent = (mod?: 'groups' | 'nextTask' | 'reference' | 'close') => {
    switch (mod) {
      case 'groups':
        return openConvalidateResponse ? (
          <Groups title="destinatario" response={openConvalidateResponse} setSelectedGroups={setSelectedGroups} />
        ) : null;
      case 'nextTask':
        return openConvalidateResponse ? (
          <NextTask
            title="destinatario"
            response={openConvalidateResponse}
            setSelectedTask={updateSelectedTask}
            setSelectedGroups={setSelectedGroups}
          />
        ) : null;
      case 'close':
        return openConvalidateResponse ? <Close response={openConvalidateResponse} /> : null;
      case 'reference':
        return openConvalidateResponse ? (
          <Reference
            response={openConvalidateResponse}
            setSelectedDefinition={setSelectedDefinition}
            setSelectedTask={updateSelectedTask}
            setSelectedGroups={setSelectedGroups}
            setSelectedTab={setSelectedTab}
          />
        ) : null;
      default:
        return null;
    }
  };

  const disableButton = () => {
    if (mode === 'groups' && !selectedGroups.length) return true;
    if (mode === 'nextTask' && !selectedTask) return true;
    if (
      mode === 'reference' &&
      selectedTab === 0 &&
      !openConvalidateResponse?.referenceNextTask?.lastTask &&
      !selectedTask
    )
      return true;
    if (
      mode === 'reference' &&
      selectedTab === 1 &&
      (!selectedDefinition || (selectedDefinition?.suspendType === 2 && !selectedTask))
    )
      return true;
    return false;
  };

  return (
    <>
      <Modal.Header title={t('confirm-title')} subtitle={t('confirm-desc')} />
      <Modal.Content>
        {rowsHistory.length ? (
          <>
            <Title>{t('storico')}</Title>
            <Table id="convalida-storico-table" hasPagination hasSort columns={storicoColumns} rows={rowsHistory} />
          </>
        ) : null}
        {mode ? renderContent(mode) : null}
        <Section>
          <Title>{t('note')}</Title>
          <TextArea defaultValue={noteLog} name="Nota" placeholder={t('note here')} onChange={onNoteChange} />
        </Section>
      </Modal.Content>
      <Modal.Footer confirmText={t('continue')} confirmAction={handleConfirm} confirmDisabled={disableButton()} />
    </>
  );
};

export default Convalida;
